import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/auth'
import { tokenManager } from '@/services/tokenManager'

/**
 * Authentication Guard
 * 
 * Protects routes that require authentication.
 * - Checks if user is authenticated
 * - Attempts token refresh if token is expired
 * - Redirects to login if authentication fails
 * - Stores intended destination for post-login redirect
 */
export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()

  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    // Store intended destination for post-login redirect
    sessionStorage.setItem('intended_destination', to.fullPath)
    
    // Redirect to login
    next({ name: 'login' })
    return
  }

  // Check if token is expired
  if (authStore.isTokenExpired) {
    // Attempt to refresh token
    const refreshToken = tokenManager.getRefreshToken()
    
    if (refreshToken) {
      try {
        // Try to refresh the token
        const tokens = await authService.refreshToken(refreshToken)
        
        // Store new tokens
        await tokenManager.storeTokens({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          idToken: tokens.id_token,
        })
        
        // Update auth store
        authStore.setTokens({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          idToken: tokens.id_token,
        })
        
        // Allow navigation
        next()
        return
      } catch (error) {
        console.error('Token refresh failed:', error)
        
        // Clear auth state
        authStore.clearAuth()
        tokenManager.clearTokens()
        
        // Store intended destination
        sessionStorage.setItem('intended_destination', to.fullPath)
        
        // Redirect to login
        next({ name: 'login' })
        return
      }
    } else {
      // No refresh token available
      authStore.clearAuth()
      tokenManager.clearTokens()
      
      // Store intended destination
      sessionStorage.setItem('intended_destination', to.fullPath)
      
      // Redirect to login
      next({ name: 'login' })
      return
    }
  }

  // User is authenticated and token is valid
  next()
}
